import { Container, Divider, Grid, Typography } from "@material-ui/core";
import {
    BackspaceOutlined as BackspaceOutlinedIcon,
    Print as PrintIcon
} from "@material-ui/icons";
import { useSnackbar } from "notistack";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import SelectSupplier from "../../components/Form/SelectSupplier";
import Success from "../../components/Form/Success";
import DateField from "../../components/Form/Date";
import ButtonUI from "../../components/ui/ButtonUI";
import LoadingUI from "../../components/ui/LoadingUI";
import PageTitle from "../../components/ui/PageTitleUI";
import { useAuth } from "../../hooks/auth";
import { PendingXMLRequestParams } from "../../interfaces/Report";
import ReportApi from "../../services/ReportApi";
import permissions from "../../utils/Permissions";
import useKeypress from "../../utils/useKeypress";
import { useStyles } from "../Shed/Style";
import Select from '../../components/Form/Select';
import * as Yup from 'yup'

const opcoes = [
    { url: "/Shed", descricao: "Incluir", state: { openPanelOnLoad: true } },
    { url: "/Shed", descricao: "Pesquisar", state: {} },
];

interface Params {
    openPanelOnLoad: boolean;
}

const PendingXMLRequest: React.FC = () => {
    const classes = useStyles();
    const location = useLocation<Params>();
    const [loading, setLoading] = useState(false);
    const { user, hasPermission } = useAuth();
    const { t: _t } = useTranslation("General");
    const { t } = useTranslation("PendingXMLRequest");
    const { t: tVal } = useTranslation("Validation");
    const history = useHistory();
    const { enqueueSnackbar } = useSnackbar();
    const [success] = useState(false);

    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => {
        if (!hasPermission(permissions.REPORTS.PendingXMLRequest))
            history.replace("/NoPermission");
    }, [history, user]);

    //Campos do Form
    interface IFormPanel {
        suppliers: any[];
        initialDate: Date | null;
        endDate: Date | null;
        renderType: any;
    }

    //Metodos do form:
    const { handleSubmit, reset, control, setError, clearErrors } = useForm<IFormPanel>({
        defaultValues: {
            suppliers: [],
            initialDate: null,
            endDate: null,
            renderType: {code: '2', label: 'Excel'}
         },
    });

    const onSubmit = async (data: IFormPanel) => {
        generateReport(data);
    };

    function validateReportRequestNullFields(data: IFormPanel) {
        return !data.initialDate;
    }

    const cleanFilters = () => {
        reset({
            suppliers: [],
            initialDate: null,
            endDate: null,
        });
    };

    const formValidation = async (data: any) => {
        var result = false
        clearErrors()
        try {
            const schema = Yup.object().shape({
                initialDate: Yup.date().nullable().typeError(tVal("formatDate")),
                endDate: Yup.date().nullable().typeError(tVal("formatDate")),
            });

            await schema.validate(data, {
                abortEarly: false,
            });

            result = true
        }
        catch (err) {

            var errors: string[] = []

            if (err instanceof Yup.ValidationError) {
                err.inner.forEach(error => {

                    if (errors.filter(element => element.includes(error.message)).length === 0)
                        errors.push(error.message)

                    setError(error.path?.split('.')[0] as "initialDate" | "endDate"
                    , { message: error.message });
                })
            }

            result = false;
        }

        return result
    }


    const generateReport = async (data: IFormPanel) => {
        
        var validForm = await formValidation(data)
        if (!validForm)
            return;

        if (validateReportRequestNullFields(data)) {
            enqueueSnackbar(_t("requiredFieldsValidation"), {
                variant: "warning",
            });
            return;
        }
        
        setLoading(true)

        var params: PendingXMLRequestParams = {
            supplierIds: data.suppliers?.map(x => x.code),
            companyId: +user.company.companyID,
            initialDate: data.initialDate,
            endDate: data.endDate,
            userName: user.name,
            renderType: data.renderType.code
        };

        ReportApi.printPendingXMLRequest(params)
            .catch(err => enqueueSnackbar(err.message, { variant: 'warning' }))
            .finally(() => setLoading(false));
    }

    if (success)
        return (
            <Container maxWidth={false} component="main">
                <div className={classes.rootContent}>
                    <div className={classes.paper}>
                        <Success opcoes={opcoes} />
                    </div>
                </div>
            </Container>
        );
    else
        return (
            <Container maxWidth={false} component="main">
                <PageTitle title={t("pageTitle")} />
                <div className={classes.rootContent}>
                    <div className={classes.paper}>
                        <form
                            translate={undefined}
                            onSubmit={handleSubmit(onSubmit)}
                        >
                            <Grid container spacing={1}>
                                <Grid
                                    item
                                    xs={6}
                                    style={{
                                        padding: "0px",
                                        display: "flex",
                                        alignItems: "center",
                                    }}
                                >
                                    <Typography className={classes.root}>
                                        {t("parameters")}
                                    </Typography>
                                </Grid>

                                <Grid
                                    item
                                    xs={6}
                                    style={{
                                        padding: "0px",
                                        textAlign: "right",
                                    }}
                                >
                                    <ButtonUI
                                        onClick={() => cleanFilters()}
                                        startIcon={<BackspaceOutlinedIcon />}
                                    >
                                        {_t("clean")}
                                    </ButtonUI>
                                    <ButtonUI
                                        type="submit"
                                        color="primary"
                                        startIcon={<PrintIcon />}
                                    >
                                        {_t("print")}
                                    </ButtonUI>
                                </Grid>
                            </Grid>

                            <Divider style={{ marginTop: "10px" }} />

                            <Grid container spacing={1}>
                                <Grid item xs={3}>
                                    <SelectSupplier
                                        name={"suppliers"}
                                        label={t("suppliers")}
                                        control={control}
                                        multiple={true}
                                        style={{ width: "100%" }}
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <DateField
                                        label={t("initialDate")}
                                        name="initialDate"
                                        InputLabelProps={{ shrink: true }}
                                        control={control}
                                        fullWidth
                                    />
                                </Grid>

                                <Grid item xs={2}>
                                    <DateField
                                        label={t("endDate")}
                                        name="endDate"
                                        InputLabelProps={{ shrink: true }}
                                        control={control}
                                        fullWidth
                                    />
                                </Grid>


                            <Grid item xs={1}>
                                <Select
                                    label={_t("reportExportType")} 
                                    name="renderType"
                                    control={control} 
                                    options={[{code: '2', label: 'Excel'}, {code: '1', label: 'PDF'}]}
                                    style={{width: '100%'}}/>
                            </Grid>

                                <Grid item xs={7}></Grid>
                            </Grid>
                        </form>
                        { loading && <LoadingUI /> }
                    </div>
                </div>
            </Container>
        );
};
export default PendingXMLRequest;
